import { createContext, useContext, type CSSProperties, type ReactNode } from 'react';

/**
 * Rail section filter — the mini-rail acts as a per-section filter for the
 * Tools tab instead of a scroll-jump: clicking "Quality" shows only the
 * quality cards, clicking it again shows the whole stack. (07-14 mtg follow-up
 * on Row 23: the collapse shipped, but the rail was still one long scroll
 * column; this is the "only that section" half of the ask.)
 *
 * The filter value is a mini-rail ITEM id ('score' | 'quality' | ...), not a
 * card's data-rail-section id — several cards belong to one mini-rail item
 * (e.g. both link cards under "Links", SERP + Summary + Structured data +
 * Publishing under "Meta").
 */

export type RailFilterId =
  | 'score'
  | 'quality'
  | 'structure'
  | 'images'
  | 'meta'
  | 'links'
  | 'social';

/** Human label per filter, for the "Filtered: X" chip. */
// eslint-disable-next-line react-refresh/only-export-components
export const RAIL_FILTER_LABELS: Record<RailFilterId, string> = {
  score: 'Score',
  quality: 'Quality',
  structure: 'Outline',
  images: 'Images',
  meta: 'Meta',
  links: 'Links',
  social: 'Social',
};

/**
 * data-rail-section id -> owning filter group. Lets jump events that carry a
 * CARD id (the issue-badge field cursor in UniversalEditorPage dispatches
 * 'serp' / 'social' / 'summary') focus the right group.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const FILTER_BY_SECTION_ID: Record<string, RailFilterId> = {
  stats: 'score',
  coverage: 'score',
  quality: 'quality',
  trust: 'quality',
  structure: 'structure',
  'hero-image': 'images',
  images: 'images',
  serp: 'meta',
  summary: 'meta',
  'structured-data': 'meta',
  publishing: 'meta',
  links: 'links',
  social: 'social',
};

/** Fired by EditorRail whenever the active filter changes (detail: {filter}). */
export const RAIL_FILTER_EVENT = 'ed-rail-filter';

/** null = no filter, the whole card stack is visible. */
// eslint-disable-next-line react-refresh/only-export-components
export const RailFilterContext = createContext<RailFilterId | null>(null);

const VISIBLE: CSSProperties = { display: 'contents' };
const HIDDEN: CSSProperties = { display: 'none' };

export interface RailFilterSlotProps {
  /**
   * Filters under which this slot is visible. An empty array means the slot
   * only appears in the unfiltered ("show all") view — used for cards with no
   * mini-rail home (Suggestions, trailing signoff).
   */
  under: RailFilterId[];
  children: ReactNode;
}

/**
 * Visibility wrapper for one card (or group of nodes) in the Tools stack.
 * Children stay MOUNTED either way — `display: none` vs `display: contents` —
 * so card-local state (drafts, collapse state, scroll) survives filtering,
 * matching how the rail already keeps tab panels mounted. `display: contents`
 * keeps visible children direct flex items of the panel, preserving the gap-3
 * stack layout and the data-rail-section scroll targets.
 */
export function RailFilterSlot({ under, children }: RailFilterSlotProps) {
  const filter = useContext(RailFilterContext);
  const visible = filter === null || under.includes(filter);
  return (
    <div data-rail-filter-slot style={visible ? VISIBLE : HIDDEN}>
      {children}
    </div>
  );
}
